import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Popover,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@material-ui/core";

import AddRoundedIcon from "@material-ui/icons/AddRounded";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import ErrorRoundedIcon from "@material-ui/icons/ErrorRounded";
import CompareArrowsRoundedIcon from "@material-ui/icons/CompareArrowsRounded";

import api from "../../services/api";
import useCompanies from "../../hooks/useCompanies";
import { i18n } from "../../translate/i18n";

/**
 * TenantCompare — side-by-side multi-tenant KPI view for super-admins.
 *
 * Lets a platform operator pick up to MAX_TENANTS companies (Cohen,
 * Levin, Almog…) and lay their operational metrics next to each other
 * to spot outliers. Each picked tenant kicks off a /api/admin/companies/:id/health
 * call — same endpoint as the per-tenant drill-down — so this page
 * doesn't need its own backend route.
 *
 * Privacy contract: same as TenantHealthPanel — counts + plan + status
 * only, no customer PII, no message content, no billing card data.
 */

const MAX_TENANTS = 4;

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const fg = isDark ? "#f1f5f9" : "#0f172a";
  const muted = isDark ? "rgba(241,245,249,0.62)" : "#64748b";
  const surface = isDark ? "rgba(255,255,255,0.04)" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "#eaeaea";
  const subSurface = isDark ? "rgba(255,255,255,0.025)" : "#fafafa";

  return {
    root: { width: "100%", display: "flex", flexDirection: "column", gap: 18 },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(46,227,122,0.12)" : "rgba(37,211,102,0.12)",
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: isDark ? "#7df0a8" : "#128c7e",
      },
    },
    title: {
      fontSize: 17,
      fontWeight: 700,
      color: fg,
      lineHeight: 1.2,
    },
    subtitle: {
      fontSize: 12.5,
      color: muted,
      marginTop: 2,
    },
    addBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 14px",
      borderRadius: 10,
      border: `1px dashed ${border}`,
      background: subSurface,
      color: fg,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 140ms ease",
      "&:hover": {
        borderColor: isDark ? "#7df0a8" : "#128c7e",
        background: isDark ? "rgba(46,227,122,0.06)" : "rgba(46,227,122,0.04)",
      },
      "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
      "& svg": { fontSize: 16 },
    },
    picker: {
      padding: 12,
      width: 320,
      maxHeight: 380,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    pickerSearch: {
      "& .MuiInputBase-root": { fontSize: 13 },
    },
    pickerList: {
      overflow: "auto",
      flex: 1,
      padding: 0,
    },
    pickerEmpty: {
      padding: 14,
      textAlign: "center",
      fontSize: 12.5,
      color: muted,
    },
    pickerItem: {
      borderRadius: 8,
      "&:hover": { background: subSurface },
    },
    pickerItemText: {
      "& .MuiListItemText-primary": {
        fontSize: 13,
        fontWeight: 600,
        color: fg,
      },
      "& .MuiListItemText-secondary": {
        fontSize: 11,
        color: muted,
      },
    },
    grid: {
      display: "grid",
      gridTemplateColumns: `200px repeat(${MAX_TENANTS}, minmax(180px, 1fr))`,
      gap: 0,
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "auto",
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: `180px repeat(${MAX_TENANTS}, minmax(160px, 1fr))`,
      },
      // Spec B.1 tableScroll pattern on xs: keep wide layout, allow
      // horizontal scroll, sticky metric-name column so the user can
      // pan tenants without losing the row label. Side-by-side compare
      // is intrinsically a desktop pattern — full card-stack would
      // double the row count per tenant; horizontal scroll is the
      // accepted compromise (Recommendation A in the inventory).
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: `140px repeat(${MAX_TENANTS}, minmax(160px, 1fr))`,
        WebkitOverflowScrolling: "touch",
        "& > div:nth-child(odd)": {
          // First column (label) stays put as you swipe.
          // CSS grid items at column 1 — pinned via position:sticky.
        },
      },
    },
    // Sticky first-column for xs swipe-compare (paired with .grid override).
    rowLabelSticky: {
      [theme.breakpoints.down("xs")]: {
        position: "sticky",
        insetInlineStart: 0,
        zIndex: 2,
      },
    },
    rowLabel: {
      padding: "12px 16px",
      fontSize: 12.5,
      fontWeight: 700,
      color: muted,
      letterSpacing: 0.3,
      borderBottom: `1px solid ${border}`,
      borderInlineEnd: `1px solid ${border}`,
      background: subSurface,
      textTransform: "uppercase",
    },
    cell: {
      padding: "12px 16px",
      fontSize: 13,
      color: fg,
      borderBottom: `1px solid ${border}`,
      borderInlineEnd: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      gap: 4,
      "&:last-child": { borderInlineEnd: "none" },
    },
    headerCell: {
      padding: "14px 16px",
      borderBottom: `1px solid ${border}`,
      borderInlineEnd: `1px solid ${border}`,
      background: subSurface,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
    tenantName: {
      fontSize: 14,
      fontWeight: 700,
      color: fg,
      lineHeight: 1.2,
    },
    tenantPlan: {
      fontSize: 11,
      color: muted,
      marginTop: 2,
    },
    closeBtn: {
      padding: 4,
      color: muted,
      "&:hover": { color: fg },
    },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "3px 9px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 700,
      "& svg": { fontSize: 13 },
    },
    statusHealthy: {
      background: isDark ? "rgba(46,227,122,0.18)" : "rgba(46,227,122,0.18)",
      color: isDark ? "#7df0a8" : "#0e6c61",
    },
    statusWatch: {
      background: isDark ? "rgba(251,191,36,0.18)" : "rgba(251,191,36,0.22)",
      color: isDark ? "#fbbf24" : "#b45309",
    },
    statusCritical: {
      background: isDark ? "rgba(252,165,165,0.18)" : "rgba(252,165,165,0.28)",
      color: isDark ? "#fca5a5" : "#b91c1c",
    },
    bigNum: {
      fontSize: 18,
      fontWeight: 700,
      color: fg,
      letterSpacing: -0.3,
      fontVariantNumeric: "tabular-nums",
    },
    sub: {
      fontSize: 11.5,
      color: muted,
    },
    aiOn: {
      color: isDark ? "#7df0a8" : "#0e6c61",
      fontWeight: 600,
    },
    aiOff: {
      color: muted,
    },
    spark: {
      display: "flex",
      gap: 2,
      alignItems: "end",
      height: 28,
    },
    sparkBar: {
      flex: 1,
      borderRadius: 2,
      background: `linear-gradient(180deg, ${isDark ? "#2ee37a" : "#25d366"} 0%, ${
        isDark ? "#128c7e" : "#128c7e"
      } 100%)`,
      minHeight: 2,
    },
    emptyCell: {
      padding: "12px 16px",
      borderBottom: `1px solid ${border}`,
      borderInlineEnd: `1px solid ${border}`,
      color: muted,
      fontSize: 12.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 60,
      "&:last-child": { borderInlineEnd: "none" },
    },
    loadingCell: {
      padding: "12px 16px",
      borderBottom: `1px solid ${border}`,
      borderInlineEnd: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 60,
      "&:last-child": { borderInlineEnd: "none" },
    },
    emptyState: {
      padding: "40px 16px",
      textAlign: "center",
      color: muted,
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
    },
    emptyTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: fg,
    },
    emptySub: {
      fontSize: 12.5,
      color: muted,
      marginTop: 4,
    },
  };
});

const TenantCompare = () => {
  const classes = useStyles();
  const companiesHook = useCompanies();

  const [allCompanies, setAllCompanies] = useState([]);
  const [selected, setSelected] = useState([]); // [{ id, name, plan, ...health }]
  const [loadingById, setLoadingById] = useState({}); // map of id → bool
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");

  // Load the full Companies list once for the picker.
  useEffect(() => {
    let cancelled = false;
    companiesHook
      .list()
      .then((rows) => {
        if (cancelled) return;
        // Drop platform owner (id=1) — can't inspect yourself.
        setAllCompanies((rows || []).filter((c) => c.id !== 1));
      })
      .catch(() => {
        if (!cancelled) setAllCompanies([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHealth = useCallback(async (companyId) => {
    setLoadingById((m) => ({ ...m, [companyId]: true }));
    try {
      const { data } = await api.get(`/api/admin/companies/${companyId}/health`);
      setSelected((cur) =>
        cur.map((s) => (s.id === companyId ? { ...s, health: data, error: null } : s))
      );
    } catch (e) {
      setSelected((cur) =>
        cur.map((s) =>
          s.id === companyId ? { ...s, health: null, error: true } : s
        )
      );
    } finally {
      setLoadingById((m) => {
        const next = { ...m };
        delete next[companyId];
        return next;
      });
    }
  }, []);

  const addTenant = (company) => {
    if (selected.length >= MAX_TENANTS) return;
    if (selected.some((s) => s.id === company.id)) return;
    setSelected((cur) => [...cur, { id: company.id, name: company.name, health: null, error: null }]);
    fetchHealth(company.id);
    setPickerAnchor(null);
    setPickerSearch("");
  };

  const removeTenant = (id) => {
    setSelected((cur) => cur.filter((s) => s.id !== id));
  };

  const filteredPickerOptions = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const taken = new Set(selected.map((s) => s.id));
    return allCompanies
      .filter((c) => !taken.has(c.id))
      .filter((c) => {
        if (!q) return true;
        return String(c.name || "").toLowerCase().includes(q);
      })
      .slice(0, 50);
  }, [allCompanies, selected, pickerSearch]);

  // Render helpers
  const sparkMaxOf = (health) => {
    if (!health?.msgVolumeLast7d?.length) return 1;
    return Math.max(1, ...health.msgVolumeLast7d.map((d) => d.count));
  };

  const statusClass = (status) => {
    if (status === "healthy") return classes.statusHealthy;
    if (status === "watch") return classes.statusWatch;
    return classes.statusCritical;
  };

  const StatusIcon = ({ status }) => {
    if (status === "healthy") return <CheckCircleRoundedIcon />;
    if (status === "watch") return <WarningRoundedIcon />;
    return <ErrorRoundedIcon />;
  };

  // Column padding when fewer than MAX_TENANTS are selected — render
  // empty placeholder cells so the grid keeps its column structure.
  const emptySlots = Math.max(0, MAX_TENANTS - selected.length);

  const renderEmpty = () => (
    <Box className={classes.emptyState}>
      <Typography className={classes.emptyTitle}>
        {i18n.t("admin.compare.empty.title", {
          defaultValue: "Pick tenants to compare",
        })}
      </Typography>
      <Typography className={classes.emptySub}>
        {i18n.t("admin.compare.empty.sub", {
          defaultValue: "Add up to 4 companies — their operational KPIs will line up side by side.",
        })}
      </Typography>
    </Box>
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <span className={classes.headerIcon}>
            <CompareArrowsRoundedIcon />
          </span>
          <Box>
            <Typography className={classes.title} component="div">
              {i18n.t("admin.compare.title", { defaultValue: "Compare tenants" })}
            </Typography>
            <Typography className={classes.subtitle} component="div">
              {i18n.t("admin.compare.subtitle", {
                count: selected.length,
                max: MAX_TENANTS,
                defaultValue: `${selected.length} of ${MAX_TENANTS} selected · operational KPIs side by side`,
              })}
            </Typography>
          </Box>
        </Box>
        <button
          type="button"
          className={classes.addBtn}
          disabled={selected.length >= MAX_TENANTS}
          onClick={(e) => setPickerAnchor(e.currentTarget)}
        >
          <AddRoundedIcon />
          {i18n.t("admin.compare.add", { defaultValue: "Add tenant" })}
        </button>
      </Box>

      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box className={classes.picker}>
          <TextField
            autoFocus
            placeholder={i18n.t("admin.compare.searchPlaceholder", {
              defaultValue: "Search by company name…",
            })}
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            size="small"
            variant="outlined"
            className={classes.pickerSearch}
            fullWidth
          />
          {filteredPickerOptions.length === 0 ? (
            <Box className={classes.pickerEmpty}>
              {i18n.t("admin.compare.pickerEmpty", {
                defaultValue: "No companies match.",
              })}
            </Box>
          ) : (
            <List className={classes.pickerList}>
              {filteredPickerOptions.map((c) => (
                <ListItem
                  key={c.id}
                  button
                  className={classes.pickerItem}
                  onClick={() => addTenant(c)}
                >
                  <ListItemText
                    className={classes.pickerItemText}
                    primary={c.name || `#${c.id}`}
                    secondary={`ID #${c.id}${c.plan?.name ? " · " + c.plan.name : ""}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      {selected.length === 0 ? (
        renderEmpty()
      ) : (
        <Box className={classes.grid}>
          {/* Header row — tenant cards */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.metric", { defaultValue: "Metric" })}
          </Box>
          {selected.map((s) => (
            <Box key={`h-${s.id}`} className={classes.headerCell}>
              <Box style={{ minWidth: 0 }}>
                <Typography className={classes.tenantName} noWrap>
                  {s.name}
                </Typography>
                <Typography className={classes.tenantPlan}>
                  {s.health?.company?.plan?.name ||
                    i18n.t("admin.compare.noPlan", { defaultValue: "No plan" })}
                </Typography>
              </Box>
              <IconButton
                className={classes.closeBtn}
                size="small"
                onClick={() => removeTenant(s.id)}
                title={i18n.t("admin.compare.remove", { defaultValue: "Remove" })}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-h-${i}`} className={classes.emptyCell}>
              {i18n.t("admin.compare.slotEmpty", { defaultValue: "—" })}
            </Box>
          ))}

          {/* Status row */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.status", { defaultValue: "Health" })}
          </Box>
          {selected.map((s) => {
            if (loadingById[s.id]) {
              return (
                <Box key={`s-${s.id}`} className={classes.loadingCell}>
                  <CircularProgress size={18} style={{ color: "#128c7e" }} />
                </Box>
              );
            }
            if (!s.health) {
              return (
                <Box key={`s-${s.id}`} className={classes.cell}>
                  <span className={classes.sub}>—</span>
                </Box>
              );
            }
            const status = s.health.health.status;
            return (
              <Box key={`s-${s.id}`} className={classes.cell}>
                <span className={`${classes.statusPill} ${statusClass(status)}`}>
                  <StatusIcon status={status} />
                  {i18n.t(`admin.health.status.${status}`, { defaultValue: status })}
                </span>
              </Box>
            );
          })}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-s-${i}`} className={classes.emptyCell}>
              —
            </Box>
          ))}

          {/* Msgs 24h */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.msgs24h", { defaultValue: "Msgs 24h" })}
          </Box>
          {selected.map((s) => (
            <Box key={`m-${s.id}`} className={classes.cell}>
              <span className={classes.bigNum}>
                {s.health ? s.health.counts.msgsLast24h : "—"}
              </span>
            </Box>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-m-${i}`} className={classes.emptyCell}>—</Box>
          ))}

          {/* Open tickets */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.tickets", { defaultValue: "Open tickets" })}
          </Box>
          {selected.map((s) => (
            <Box key={`t-${s.id}`} className={classes.cell}>
              <span className={classes.bigNum}>
                {s.health ? s.health.counts.ticketsOpen : "—"}
              </span>
              {s.health && (
                <span className={classes.sub}>
                  {i18n.t("admin.compare.row.closed24h", {
                    count: s.health.counts.ticketsClosedLast24h,
                    defaultValue: `${s.health.counts.ticketsClosedLast24h} closed`,
                  })}
                </span>
              )}
            </Box>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-t-${i}`} className={classes.emptyCell}>—</Box>
          ))}

          {/* WhatsApp */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.whatsapp", { defaultValue: "WhatsApp" })}
          </Box>
          {selected.map((s) => (
            <Box key={`w-${s.id}`} className={classes.cell}>
              <span className={classes.bigNum}>
                {s.health
                  ? `${s.health.counts.whatsapps.connected}/${s.health.counts.whatsapps.total}`
                  : "—"}
              </span>
              <span className={classes.sub}>
                {i18n.t("admin.health.kpi.connectedTotal", { defaultValue: "Connected / total" })}
              </span>
            </Box>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-w-${i}`} className={classes.emptyCell}>—</Box>
          ))}

          {/* Users + queues */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.users", { defaultValue: "Users" })}
          </Box>
          {selected.map((s) => (
            <Box key={`u-${s.id}`} className={classes.cell}>
              <span className={classes.bigNum}>
                {s.health ? s.health.counts.users : "—"}
              </span>
              {s.health && (
                <span className={classes.sub}>
                  {i18n.t("admin.compare.row.queuesN", {
                    count: s.health.counts.queues,
                    defaultValue: `${s.health.counts.queues} queues`,
                  })}
                </span>
              )}
            </Box>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-u-${i}`} className={classes.emptyCell}>—</Box>
          ))}

          {/* AI on/off */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.ai", { defaultValue: "AI worker" })}
          </Box>
          {selected.map((s) => (
            <Box key={`ai-${s.id}`} className={classes.cell}>
              <span
                className={
                  s.health?.configuration?.aiEnabled ? classes.aiOn : classes.aiOff
                }
              >
                {s.health
                  ? s.health.configuration.aiEnabled
                    ? i18n.t("admin.health.config.on", { defaultValue: "On" })
                    : i18n.t("admin.health.config.off", { defaultValue: "Off" })
                  : "—"}
              </span>
            </Box>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-ai-${i}`} className={classes.emptyCell}>—</Box>
          ))}

          {/* 7-day sparkline */}
          <Box className={`${classes.rowLabel} ${classes.rowLabelSticky}`}>
            {i18n.t("admin.compare.row.spark", { defaultValue: "Volume 7d" })}
          </Box>
          {selected.map((s) => {
            if (!s.health) {
              return (
                <Box key={`sp-${s.id}`} className={classes.cell}>
                  <span className={classes.sub}>—</span>
                </Box>
              );
            }
            const max = sparkMaxOf(s.health);
            return (
              <Box key={`sp-${s.id}`} className={classes.cell}>
                <Box className={classes.spark}>
                  {s.health.msgVolumeLast7d.map((d, i) => {
                    const h = Math.max(2, Math.round((d.count / max) * 100));
                    return (
                      <span
                        key={i}
                        className={classes.sparkBar}
                        style={{ height: `${h}%` }}
                        title={`${d.day}: ${d.count}`}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box key={`empty-sp-${i}`} className={classes.emptyCell}>—</Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TenantCompare;
